import { createContext, useContext, useState, useEffect } from "react";
import { RenderMenu, RenderRoutes } from '../components/structure/RenderNavigation';
import { RenderFooter } from '../components/structure/Footer';
import { t } from "i18next";
import authService from '../services/authService';
import { useLocation, useNavigate } from 'react-router-dom';

const AuthContext = createContext();
export const AuthData = () => useContext(AuthContext);

// Cookie yardımcı fonksiyonları
const setCookie = (name, value, days) => {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Strict`;
};

const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
};

const deleteCookie = (name) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict`;
};

// Sayfa yüklendiğinde cookie'den oturumu senkron olarak restore et
const restoreSessionFromCookie = () => {
    // sessionStorage zaten doluysa kullan
    if (sessionStorage.getItem("jwtKey") && sessionStorage.getItem("jwtKey").length > 0) {
        return { 
            name: sessionStorage.getItem("ePosta") || sessionStorage.getItem("eposta"), 
            isAuthenticated: true,
            rizaOnay: sessionStorage.getItem("rizaOnay") === "1"
        };
    }

    // Cookie kontrol et (Beni Hatırla)
    const savedSession = getCookie("kama3d_session");
    if (savedSession) {
        try {
            const data = JSON.parse(savedSession);
            if (data.jwtKey && data.jwtKey.length > 0) {
                sessionStorage.setItem("jwtKey", data.jwtKey);
                sessionStorage.setItem("refreshToken", data.refreshToken || "");
                sessionStorage.setItem("eposta", data.eposta || data.ePosta);
                sessionStorage.setItem("ePosta", data.eposta || data.ePosta); // Geriye dönük uyumluluk için ikisini de set edelim
                sessionStorage.setItem("activationKey", data.activationKey);
                sessionStorage.setItem("userId", data.userId);
                sessionStorage.setItem("rizaOnay", data.rizaOnay ? "1" : "0");
                localStorage.setItem("eposta", data.eposta || data.ePosta);
                return { 
                    name: data.eposta || data.ePosta, 
                    isAuthenticated: true,
                    rizaOnay: data.rizaOnay
                };
            }
        } catch (e) {
            deleteCookie("kama3d_session");
        }
    }

    return { name: '', isAuthenticated: false, rizaOnay: false };
};

export const AuthWrapper = () => {

    const [user, setUser] = useState(restoreSessionFromCookie)
    const [isRefreshing, setIsRefreshing] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // KVKK Onay kontrolü ve yönlendirme
    useEffect(() => {
        const riza = sessionStorage.getItem("rizaOnay") === "1";
        const publicAuthPages = ['/login', '/forgotpassword', '/addmember', '/activation'];
        
        if (user.isAuthenticated && !riza) {
            // Eğer onay yoksa ve açık sayfalardan birinde veya myaccount'da değilse zorla yönlendir
            if (!publicAuthPages.includes(location.pathname) && location.pathname !== "/myaccount") {
                navigate("/myaccount", { state: { forceRiza: true }, replace: true });
            }
        }
    }, [user.isAuthenticated, location.pathname, navigate]);

    useEffect(() => {
        const handleRefreshState = (e) => {
            setIsRefreshing(e.detail);
        };
        window.addEventListener('api-refresh-state', handleRefreshState);
        return () => window.removeEventListener('api-refresh-state', handleRefreshState);
    }, []);

    // Oturumu açılmış kullanıcının login/register sayfalarına girmesini engelle
    useEffect(() => {
        const publicAuthPages = ['/login', '/forgotpassword', '/addmember'];
        if (user.isAuthenticated && publicAuthPages.includes(location.pathname)) {
            navigate("/", { replace: true });
        }
    }, [user.isAuthenticated, location.pathname, navigate]);

    const login = async (userName, password, rememberMe = false) => {
        try {
            const resp = await authService.login(userName, password);

            if (resp.jwtKey && resp.jwtKey.length > 0) {
                // Verileri hem küçük hem büyük P ile kaydedelim ki diğer bileşenler kırılmasın
                sessionStorage.setItem("jwtKey", resp.jwtKey)
                sessionStorage.setItem("refreshToken", resp.refreshToken || "")
                sessionStorage.setItem("eposta", resp.eposta)
                sessionStorage.setItem("ePosta", resp.eposta) 
                sessionStorage.setItem("activationKey", resp.activationKey)
                sessionStorage.setItem("userId", resp.userId)
                sessionStorage.setItem("rizaOnay", resp.rizaOnay ? "1" : "0")
                localStorage.setItem("eposta", resp.eposta)

                // Beni Hatırla seçiliyse 30 günlük cookie yaz
                if (rememberMe) {
                    const sessionData = JSON.stringify({
                        jwtKey: resp.jwtKey,
                        refreshToken: resp.refreshToken || "",
                        eposta: resp.eposta,
                        activationKey: resp.activationKey,
                        userId: resp.userId,
                        rizaOnay: resp.rizaOnay
                    });
                    setCookie("kama3d_session", sessionData, 30);
                }

                setUser({ name: resp.eposta, isAuthenticated: true, rizaOnay: resp.rizaOnay })
                return resp;
            } else {
                sessionStorage.removeItem("jwtKey")
                sessionStorage.removeItem("eposta")
                sessionStorage.removeItem("ePosta")
                sessionStorage.removeItem("activationKey")
                sessionStorage.removeItem("userId")

                if (resp.isNotActivated) {
                    const actErr = new Error(resp.message || "Aktivasyon yapılmamış");
                    actErr.isNotActivated = true;
                    actErr.eposta = resp.eposta || userName;
                    throw actErr;
                }

                throw new Error(t('mesajGirisHatali'));
            }
        } catch (error) {
            if (error.isNotActivated) {
                throw error;
            }
            throw t('mesajGirisHatali');
        }
    }

    const logout = () => {
        sessionStorage.clear();
        deleteCookie("kama3d_session");
        setUser({ name: '', isAuthenticated: false });
        // Küçük bir gecikme ile yönlendirme yapmak bazen state güncellemeleriyle çakışmayı önler
        setTimeout(() => {
            navigate("/login");
        }, 10);
    }

    const showSidebar = !['/login', '/forgotpassword', '/addmember'].includes(location.pathname);

    return (
        <AuthContext.Provider value={{ user, setUser, login, logout, isRefreshing }}>
            {showSidebar ? (
                <div className="app-layout">
                    <RenderMenu />
                    <div className="main-content">
                        <div className="content-area">
                            <RenderRoutes />
                        </div>
                        <RenderFooter />
                    </div>
                </div>
            ) : (
                <RenderRoutes />
            )}
        </AuthContext.Provider>
    )

}